/*
 * Naturlabs homepage helpers (loaded with defer by sections/nl-home-hero.liquid).
 *
 * <rail-dots data-label="Snímek %index% z %count%">
 *   Pagination dots for the closest <scroll-rail> (foundation component in nl-app.js).
 *   Builds one button per rail item, marks the item nearest to the start edge with aria-current,
 *   scrolls to an item on click and hides itself when the track does not overflow.
 *   Respects prefers-reduced-motion and the theme motion setting through NL.motion.reduced.
 */
(() => {
  'use strict';

  if (customElements.get('rail-dots')) return;

  const reduced = () =>
    (window.NL && window.NL.motion && window.NL.motion.reduced) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class RailDots extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      const rail = this.closest('scroll-rail');
      this.track = rail ? rail.querySelector('[data-rail-track]') : null;
      if (!this.track) return;
      this._bound = true;
      this.items = Array.from(this.track.children);
      this.build();
      this.onScroll = () => this.queue();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queue());
        this.resizeObserver.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this._bound = false;
    }

    build() {
      this.replaceChildren();
      if (this.items.length < 2) return;
      const template = this.dataset.label || '%index% / %count%';
      const count = String(this.items.length);
      this.buttons = this.items.map((item, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'nl-dots__dot';
        button.setAttribute('aria-label', template.replace('%index%', String(index + 1)).replace('%count%', count));
        if (this.track.id) button.setAttribute('aria-controls', this.track.id);
        button.addEventListener('click', () => this.go(index));
        this.appendChild(button);
        return button;
      });
    }

    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    startEdge() {
      const style = getComputedStyle(this.track);
      const padding = parseFloat(style.scrollPaddingInlineStart || style.scrollPaddingLeft) || 0;
      return this.track.getBoundingClientRect().left + padding;
    }

    go(index) {
      const item = this.items[index];
      if (!item) return;
      const delta = item.getBoundingClientRect().left - this.startEdge();
      this.track.scrollBy({ left: delta, behavior: reduced() ? 'auto' : 'smooth' });
    }

    update() {
      if (!this.buttons || !this.buttons.length) return;
      const overflow = this.track.scrollWidth - this.track.clientWidth > 2;
      this.hidden = !overflow;
      if (!overflow) return;

      let active = 0;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (Math.abs(this.track.scrollLeft) >= max - 2) {
        active = this.items.length - 1;
      } else {
        const edge = this.startEdge();
        let best = Infinity;
        this.items.forEach((item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - edge);
          if (distance < best) {
            best = distance;
            active = index;
          }
        });
      }

      this.buttons.forEach((button, index) => {
        if (index === active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('rail-dots', RailDots);
})();
